import { useCallback } from "react";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { assignmentChangeInvalidationTargets } from "../queryKeys/casePathologistAssignmentQueryKeys";
import { casesService } from "../services/cases.service";
import type { AssignPathologistRequest } from "../types/casePathologistAssignment";
import { getApiErrorStatus } from "../utils/apiError";
import { useHasPermission } from "./useHasPermission";

const ASSIGN_PERMISSION = "cases.pathologist.reassign";

export function useInitialCasePathologistAssignment(caseId: string) {
  const queryClient = useQueryClient();
  const canAssignPathologist = useHasPermission(ASSIGN_PERMISSION);
  const invalidateAssignmentQueries = useCallback(async () => {
    await Promise.all(
      assignmentChangeInvalidationTargets(caseId).map((target) =>
        queryClient.invalidateQueries(target)
      )
    );
  }, [caseId, queryClient]);
  const assignMutation = useMutation({
    mutationFn: (request: AssignPathologistRequest) =>
      casesService.assignCasePathologist(caseId, request),
    onSuccess: (response) => {
      void invalidateAssignmentQueries();
      notifications.show({
        title: "Pathologist assigned",
        message: `${response.currentAssignment.pathologist.fullName} is now assigned to case ${response.caseNumber}.`,
        color: "green",
        autoClose: 4000,
      });
    },
    onError: (error) => {
      if (getApiErrorStatus(error) === 409) void invalidateAssignmentQueries();
    },
  });

  return { canAssignPathologist, assignMutation };
}
